import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Card } from "../components/ui/card";
import { VerdictBadge } from "../components/VerdictBadge";
import { FactCheckResult } from "../components/FactCheckResult";
import { NewsletterSignup } from "../components/NewsletterSignup";
import { FactOfDayWidget } from "../components/FactOfDayWidget";
import { Search, Sparkles, Zap, Shield, Heart, ArrowRight, ShieldAlert, MessageSquare, Sunrise } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { useTheme } from "../lib/theme";
import { trackFactCheck, trackVoiceCheck } from "../lib/analytics";
import { ParticleField } from "../components/ParticleField";
import { VerdictTicker } from "../components/VerdictTicker";
import { CountUp } from "../components/CountUp";
import { PhotoCheckPanel } from "../components/PhotoCheckPanel";
import { VoiceCheckButton } from "../components/VoiceCheckButton";
import TruthScoreWidget from "../components/TruthScoreWidget";
import ImpactTracker from "../components/ImpactTracker";
import TruthGlobe from "../components/TruthGlobe";
import SearchTheTruth from "../components/SearchTheTruth";
import SEO from "../components/SEO";

const CLAIM_EXAMPLES = [
    "Humans only use 10% of their brain.",
    "The Great Wall of China is visible from space with the naked eye.",
    "Drinking 8 glasses of water a day is scientifically required.",
    "Bananas are berries but strawberries aren't.",
];

const SCAM_EXAMPLES = [
    "Hi grandma, I lost my phone. Please send $500 to this Cash App tag — I'll pay you back.",
    "Your Amazon account was suspended. Verify at amaz0n-secure-verify.com in 24hrs or lose access.",
    "I'm a US soldier deployed overseas. I need $2000 for a leave request. Please send iTunes gift cards.",
    "Congrats! You've won a $1000 Walmart gift card. Click here to claim now.",
];

const PLACEHOLDERS = {
    claim: 'Paste a claim, headline, or quote… e.g. "NASA confirmed water on the moon in 2020."',
    scam: 'Paste a suspicious email, text, DM, or offer here — we\'ll tell you if it\'s a scam.',
};

export default function Landing() {
    const [mode, setMode] = useState("claim"); // claim | scam
    const [claim, setClaim] = useState("");
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [trending, setTrending] = useState([]);
    const [liveChecks, setLiveChecks] = useState([]);
    const [impact, setImpact] = useState({ raised_usd: 0, donations_count: 0, goal_usd: 100000 });
    const [fod, setFod] = useState(null);
    const [phIndex, setPhIndex] = useState(0);
    const navigate = useNavigate();
    const { user } = useAuth();
    const { layoutId } = useTheme();

    useEffect(() => {
        api.get("/trending").then((r) => setTrending(r.data.slice(0, 6))).catch(() => {});
        api.get("/homepage/live-checks?limit=6").then((r) => setLiveChecks(r.data?.items || [])).catch(() => {});
        api.get("/impact/counter").then((r) => setImpact(r.data)).catch(() => {});
        api.get("/fact-of-day").then((r) => setFod(r.data)).catch(() => {});
    }, []);

    // Cycle placeholder examples every 3.5s when input is empty
    useEffect(() => {
        if (claim.length > 0) return;
        const t = setInterval(() => {
            setPhIndex((i) => i + 1);
        }, 3500);
        return () => clearInterval(t);
    }, [claim, mode]);

    const submit = async (text) => {
        const t = (text ?? claim).trim();
        if (t.length < 3) { toast.error("Enter something to check"); return; }
        setLoading(true);
        setResult(null);
        try {
            const { data } = await api.post("/factcheck", { claim: t, mode });
            setResult(data);
            trackFactCheck(data.mode || mode, data.verdict, data.confidence);
            setTimeout(() => window.scrollTo({ top: 520, behavior: "smooth" }), 60);
        } catch (e) {
            const detail = e?.response?.data?.detail;
            if (detail?.code === "daily_limit_reached") {
                toast.error("Daily free limit reached");
                navigate("/pricing?reason=limit");
            } else {
                toast.error(typeof detail === "string" ? detail : detail?.message || "Could not run this check right now");
            }
        } finally {
            setLoading(false);
        }
    };

    const progressPct = Math.min(100, (impact.raised_usd / (impact.goal_usd || 1)) * 100);
    const examples = mode === "scam" ? SCAM_EXAMPLES : CLAIM_EXAMPLES;

    return (
        <div>
            <SEO
                title="Real-Time Fact Checking — Verify any claim in seconds"
                description="Real-time AI fact checking and scam detection. Instantly verify claims, images, and voice recordings — free, transparent, mission-driven. Every donation funds Hands of Hope."
                path="/"
                keywords="real-time fact checking, real time fact checker, AI fact checker, fact check tool, scam detector, verify claims online, misinformation detector, image fact check, voice fact check, free fact checker, hands of hope"
                jsonLd={{
                    "@context": "https://schema.org",
                    "@type": "FAQPage",
                    "mainEntity": [
                        {
                            "@type": "Question",
                            "name": "What is real-time fact checking?",
                            "acceptedAnswer": {
                                "@type": "Answer",
                                "text": "Real-time fact checking uses AI to instantly analyze a claim, image, or voice recording and return a verdict — true, false, misleading, or unverified — in seconds. Real-Check.org runs the pipeline in your browser with GPT-5.2 and OpenAI Whisper.",
                            },
                        },
                        {
                            "@type": "Question",
                            "name": "Is Real-Check.org free?",
                            "acceptedAnswer": {
                                "@type": "Answer",
                                "text": "Yes. Anonymous users get 5 free fact checks per hour. Signed-in users get 20 per day. Pro is optional at $9/month for unlimited checks and no ads.",
                            },
                        },
                        {
                            "@type": "Question",
                            "name": "How does Real-Check help charity?",
                            "acceptedAnswer": {
                                "@type": "Answer",
                                "text": "Half of every donation, Pro subscription, and merch pre-order funds Hands of Hope — a charity for people and animals founded by Chris, a disabled veteran. The other half keeps Real-Check independent and ad-free.",
                            },
                        },
                    ],
                }}
            />
            {/* HERO */}
            <section className="relative overflow-hidden pt-14 pb-20 md:pt-20 md:pb-28">
                {/* Ambient aurora waves — CSS-only, always plays */}
                <div className="aurora-hero" aria-hidden />
                {/* Aurora particles */}
                <ParticleField density={70} />
                <div
                    className="absolute inset-0 opacity-25 pointer-events-none"
                    style={{
                        backgroundImage:
                            "url(https://images.unsplash.com/photo-1757466762489-52fea38071ad?crop=entropy&cs=srgb&fm=jpg&q=85&w=1800)",
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        maskImage: "linear-gradient(to bottom, black 20%, transparent 100%)",
                        WebkitMaskImage: "linear-gradient(to bottom, black 20%, transparent 100%)",
                    }}
                />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-background/40 to-background pointer-events-none" />

                <div className="relative max-w-6xl mx-auto px-6 lg:px-10">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="max-w-3xl"
                    >
                        <div className="inline-flex items-center gap-2 rounded-full border border-primary/40 bg-primary/15 px-4 py-1.5 text-xs font-semibold text-accent mb-6" data-testid="hero-badge">
                            <Heart className="h-3 w-3 text-primary" />
                            <span>100% of donations fund Hands of Hope</span>
                        </div>
                        <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-7xl tracking-tight text-accent leading-[1.02]">
                            Are you being{" "}
                            <span className="text-primary">lied to</span>?
                            <br />
                            Find out in seconds.
                        </h1>
                        <p className="mt-6 text-lg md:text-xl text-muted-foreground max-w-2xl leading-relaxed">
                            Real-Check is your AI truth engine. Fact-check any claim <em>and</em> detect scams,
                            phishing, catfishing, or too-good-to-be-true offers — with real sources and a
                            <span className="text-accent font-semibold"> green/yellow/red</span> verdict.
                        </p>
                        <p className="mt-5 text-base text-muted-foreground/90 max-w-2xl leading-relaxed border-l-2 border-primary/50 pl-4" data-testid="hero-mission-line">
                            I built <span className="text-accent font-semibold">Real-Check.org</span> to help people and animals through{" "}
                            <span className="text-accent font-semibold">Hands of Hope</span> (founded in 2025).
                            This is a place to breathe, reset, and find clarity when life feels overwhelming.
                            {" "}<Link to="/about" className="text-primary hover:underline" data-testid="hero-read-story-link">Read the full story →</Link>
                        </p>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.15 }}
                        className={`mt-10 ${layoutId === "split" ? "" : "max-w-3xl"}`}
                    >
                        <div className={layoutId === "split" ? "grid lg:grid-cols-3 gap-8" : ""}>
                            <div className={layoutId === "split" ? "lg:col-span-2" : ""}>
                                {/* Mode selector + voice */}
                                <div className="flex flex-wrap items-center gap-3 mb-4">
                                    <div className="inline-flex p-1 rounded-full bg-white/5 border border-white/10">
                                        <button
                                            onClick={() => { setMode("claim"); setResult(null); }}
                                            data-testid="mode-claim-button"
                                            className={`flex items-center gap-2 px-5 py-2 rounded-full text-sm font-semibold transition-colors ${
                                                mode === "claim" ? "bg-primary text-primary-foreground shadow-lg shadow-primary/30" : "text-muted-foreground hover:text-accent"
                                            }`}
                                        >
                                            <MessageSquare className="h-4 w-4" /> Fact-check a claim
                                        </button>
                                        <button
                                            onClick={() => { setMode("scam"); setResult(null); }}
                                            data-testid="mode-scam-button"
                                            className={`flex items-center gap-2 px-5 py-2 rounded-full text-sm font-semibold transition-colors ${
                                                mode === "scam" ? "bg-primary text-primary-foreground shadow-lg shadow-primary/30" : "text-muted-foreground hover:text-accent"
                                            }`}
                                        >
                                            <ShieldAlert className="h-4 w-4" /> Check for a scam
                                        </button>
                                    </div>
                                    <VoiceCheckButton
                                        mode={mode}
                                        onResult={(r) => {
                                            setResult(r);
                                            trackVoiceCheck(r.verdict);
                                            setTimeout(() => window.scrollTo({ top: 520, behavior: "smooth" }), 60);
                                        }}
                                    />
                                </div>

                                <div className="relative card-surface rounded-2xl p-2 badge-glow-primary">
                                    <Textarea
                                        data-testid="hero-claim-input"
                                        value={claim}
                                        onChange={(e) => setClaim(e.target.value)}
                                        placeholder={PLACEHOLDERS[mode]}
                                        className="w-full min-h-[140px] bg-transparent border-0 text-lg placeholder:text-muted-foreground/70 focus-visible:ring-0 resize-none px-4 py-3"
                                        onKeyDown={(e) => {
                                            if ((e.metaKey || e.ctrlKey) && e.key === "Enter") submit();
                                        }}
                                    />
                                    <div className="flex items-center justify-between gap-3 px-3 pb-2">
                                        <span className="text-xs text-muted-foreground hidden sm:block">
                                            ⌘/Ctrl + Enter · powered by GPT-5.2
                                        </span>
                                        <Button
                                            size="lg"
                                            onClick={() => submit()}
                                            disabled={loading}
                                            data-testid="hero-check-button"
                                            className="rounded-full h-12 px-7 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold shadow-lg shadow-primary/30"
                                        >
                                            {loading ? (
                                                <>
                                                    <span className="pulse-dot inline-block w-2 h-2 rounded-full bg-white mr-2" />
                                                    Verifying…
                                                </>
                                            ) : mode === "scam" ? (
                                                <><Shield className="h-4 w-4 mr-2" /> Check for scam</>
                                            ) : (
                                                <><Search className="h-4 w-4 mr-2" /> Check the facts</>
                                            )}
                                        </Button>
                                    </div>
                                </div>

                                <div className="mt-5 flex flex-wrap items-center gap-2">
                                    <span className="text-xs uppercase tracking-widest text-muted-foreground mr-1">
                                        {mode === "scam" ? "Try a real scam:" : "Try:"}
                                    </span>
                                    {examples.map((ex, i) => (
                                        <button
                                            key={i}
                                            onClick={() => { setClaim(ex); submit(ex); }}
                                            data-testid={`example-claim-${i}`}
                                            className="text-xs px-3 py-1.5 rounded-full border border-white/10 text-muted-foreground hover:text-accent hover:border-primary/50 hover:bg-white/[0.03] transition-colors"
                                        >
                                            {ex.length > 60 ? ex.slice(0, 58) + "…" : ex}
                                        </button>
                                    ))}
                                </div>

                                {user && !user.is_pro && (
                                    <p className="mt-4 text-xs text-muted-foreground" data-testid="daily-usage-hint">
                                        Free tier: <span className="text-accent font-semibold">{user.daily_checks_used}/{user.daily_limit}</span> checks used today.{" "}
                                        <Link to="/pricing" className="text-primary hover:underline">Upgrade to Pro</Link> for unlimited.
                                    </p>
                                )}
                            </div>

                            {layoutId === "split" && (
                                <div className="lg:col-span-1 space-y-4" data-testid="split-sidebar">
                                    {fod && (
                                        <Link
                                            to={`/check/${fod.check_id}`}
                                            className="block card-surface rounded-2xl p-5 hover:-translate-y-0.5 transition-transform"
                                        >
                                            <div className="text-[10px] uppercase tracking-widest text-primary mb-2 flex items-center gap-1.5">
                                                <Sunrise className="h-3 w-3" /> Fact of the Day
                                            </div>
                                            <VerdictBadge verdict={fod.verdict} confidence={fod.confidence} size="sm" />
                                            <p className="mt-3 text-accent font-medium text-sm leading-snug line-clamp-3">
                                                &ldquo;{fod.claim}&rdquo;
                                            </p>
                                        </Link>
                                    )}
                                    <div className="card-surface rounded-2xl p-5">
                                        <div className="text-[10px] uppercase tracking-widest text-primary mb-3">
                                            Trending now
                                        </div>
                                        {trending.slice(0, 3).length === 0 ? (
                                            <p className="text-xs text-muted-foreground">Nothing yet — be the first.</p>
                                        ) : (
                                            <ul className="space-y-3">
                                                {trending.slice(0, 3).map((tc, i) => (
                                                    <li key={tc.check_id}>
                                                        <Link
                                                            to={`/check/${tc.check_id}`}
                                                            data-testid={`split-trending-${i}`}
                                                            className="block hover:opacity-80 transition-opacity"
                                                        >
                                                            <VerdictBadge verdict={tc.verdict} confidence={tc.confidence} size="sm" />
                                                            <p className="mt-2 text-sm text-accent/90 leading-snug line-clamp-2">
                                                                &ldquo;{tc.claim}&rdquo;
                                                            </p>
                                                        </Link>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </motion.div>

                    {result && (
                        <div className="mt-14 max-w-4xl" data-testid="hero-result-wrapper">
                            <FactCheckResult result={result} />
                        </div>
                    )}
                </div>
            </section>

            {/* LIVE VERDICT TICKER */}
            <VerdictTicker />

            {/* VERIFY BY PHOTO (main CTA right under hero) */}
            <PhotoCheckPanel />

            {/* FACT OF THE DAY (hidden on split layout — it's in the hero sidebar) */}
            {layoutId !== "split" && (
                <section className="max-w-6xl mx-auto px-6 lg:px-10 pb-16 pt-12">
                    <FactOfDayWidget />
                </section>
            )}

            {/* IMPACT STRIP */}
            <section className="border-y border-white/10 bg-[hsl(235,50%,10%)]">
                <div className="max-w-6xl mx-auto px-6 lg:px-10 py-10 grid md:grid-cols-3 gap-8 items-center">
                    <div className="md:col-span-2">
                        <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Live impact</p>
                        <h3 className="font-display text-2xl md:text-3xl text-accent" data-testid="impact-raised">
                            $<CountUp value={impact.raised_usd} decimals={0} testid="impact-raised-count" /> raised for{" "}
                            <span className="text-primary">Hands of Hope</span>
                            <span className="ml-2 text-xs uppercase tracking-widest px-2 py-1 rounded-full border border-emerald-500/40 text-emerald-400 bg-emerald-500/10 align-middle">
                                Verified charity
                            </span>
                        </h3>
                        <div className="mt-4 w-full h-2 rounded-full bg-white/5 overflow-hidden">
                            <div
                                className="h-full bg-gradient-to-r from-primary to-emerald-400 transition-all duration-1000"
                                style={{ width: `${progressPct}%` }}
                            />
                        </div>
                        <p className="text-xs text-muted-foreground mt-2">
                            <CountUp value={impact.donations_count} /> donations · goal ${impact.goal_usd.toLocaleString()}
                        </p>
                    </div>
                    <div className="flex md:justify-end">
                        <Link to="/donate">
                            <Button
                                size="lg"
                                data-testid="impact-donate-cta"
                                className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold h-12 px-6"
                            >
                                <Heart className="h-4 w-4 mr-2" /> Donate to Hands of Hope
                            </Button>
                        </Link>
                    </div>
                </div>
            </section>

            {/* DAILY TRUTH SCORE — live dashboard */}
            <TruthScoreWidget />

            {/* SEARCH THE TRUTH — real-time reality-check engine */}
            <SearchTheTruth />

            {/* IMPACT TRACKER — mission-first metrics */}
            <ImpactTracker />

            {/* TRUTH GLOBE — rotating verdict visualization */}
            <TruthGlobe />

            {/* HOW IT WORKS */}
            <section className="max-w-6xl mx-auto px-6 lg:px-10 py-24">
                <div className="max-w-2xl mb-16">
                    <p className="text-xs uppercase tracking-widest text-primary mb-3">How it works</p>
                    <h2 className="font-display text-3xl md:text-5xl text-accent tracking-tight">
                        Two modes. One truth engine.
                    </h2>
                </div>
                <div className="grid md:grid-cols-3 gap-6">
                    {[
                        { icon: Zap, title: "Instant AI verdict", body: "GPT-5.2 analyzes the claim or message, cross-references public evidence, and returns a verdict in seconds." },
                        { icon: ShieldAlert, title: "Scam & phishing detection", body: "Paste a suspicious email, DM, dating-app message, or offer. We spot the red flags — impersonation, urgency, gift-card asks, lookalike domains — and tell you what to do." },
                        { icon: Sparkles, title: "Sources on every result", body: "Every check ships with 3–5 cited sources — Reuters, AP, FTC, FBI IC3, PolitiFact, Snopes — each rated for credibility." },
                    ].map((f, i) => (
                        <Card key={i} className="card-surface p-8 rounded-2xl hover:-translate-y-1 transition-transform">
                            <div className="w-12 h-12 rounded-xl bg-primary/20 border border-primary/40 flex items-center justify-center mb-6">
                                <f.icon className="h-5 w-5 text-accent" />
                            </div>
                            <h3 className="font-display text-xl text-accent mb-2">{f.title}</h3>
                            <p className="text-muted-foreground leading-relaxed text-sm">{f.body}</p>
                        </Card>
                    ))}
                </div>
            </section>

            {/* AUTO FACT-CHECK FEED — refreshed on a schedule by the backend
                self-generator; real backend output, no manual clicks required */}
            {liveChecks.length > 0 && (
                <section className="max-w-6xl mx-auto px-6 lg:px-10 pb-4" data-testid="live-checks-section">
                    <div className="flex items-end justify-between mb-8">
                        <div>
                            <p className="text-xs uppercase tracking-widest text-primary mb-2">Auto-generated · Live feed</p>
                            <h2 className="font-display text-3xl md:text-4xl text-accent">Fresh from the fact-checker</h2>
                            <p className="text-sm text-muted-foreground mt-2">
                                Our AI fact-checker runs on a schedule so you always see something new — no clicks required.
                            </p>
                        </div>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {liveChecks.map((lc, i) => (
                            <div
                                key={lc.check_id}
                                data-testid={`live-check-card-${i}`}
                                className="card-surface p-6 rounded-2xl hover:-translate-y-1 transition-transform"
                            >
                                <VerdictBadge verdict={lc.verdict} confidence={lc.confidence} size="sm" />
                                <p className="mt-4 text-accent font-medium leading-snug line-clamp-3">
                                    &ldquo;{lc.claim}&rdquo;
                                </p>
                                <p className="mt-3 text-xs text-muted-foreground line-clamp-3">
                                    {lc.summary}
                                </p>
                                <p className="mt-4 text-[10px] uppercase tracking-widest text-primary/70">
                                    {lc.category || "General"}
                                </p>
                            </div>
                        ))}
                    </div>
                </section>
            )}

            {/* TRENDING */}
            <section className="max-w-6xl mx-auto px-6 lg:px-10 pb-20">
                <div className="flex items-end justify-between mb-8">
                    <div>
                        <p className="text-xs uppercase tracking-widest text-primary mb-2">Trending today</p>
                        <h2 className="font-display text-3xl md:text-4xl text-accent">Fresh from the community</h2>
                    </div>
                    <Link to="/trending" className="text-sm text-primary hover:text-accent flex items-center gap-1" data-testid="see-all-trending-link">
                        See all <ArrowRight className="h-4 w-4" />
                    </Link>
                </div>
                {trending.length === 0 ? (
                    <Card className="card-surface p-10 text-center text-muted-foreground rounded-2xl">
                        No fact-checks yet. Be the first — try the search above.
                    </Card>
                ) : (
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {trending.map((tc, i) => (
                            <Link
                                key={tc.check_id}
                                to={`/check/${tc.check_id}`}
                                data-testid={`trending-card-${i}`}
                                className="card-surface p-6 rounded-2xl hover:-translate-y-1 transition-transform block"
                            >
                                <VerdictBadge verdict={tc.verdict} confidence={tc.confidence} size="sm" />
                                <p className="mt-4 text-accent font-medium leading-snug line-clamp-3">
                                    &ldquo;{tc.claim}&rdquo;
                                </p>
                                <p className="mt-3 text-xs text-muted-foreground line-clamp-2">
                                    {tc.summary}
                                </p>
                            </Link>
                        ))}
                    </div>
                )}
            </section>

            {/* NEWSLETTER */}
            <section className="max-w-3xl mx-auto px-6 lg:px-10 pb-24">
                <NewsletterSignup source="landing-bottom" />
            </section>
        </div>
    );
}
